import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { cn } from '@elbklar/ui';
import { searchApi, type SearchResult } from '@/lib/api';

/**
 * Die globale Suche des CRM (GET /search: Kunden, Leads, Partner, Kontakte). Eine
 * Quelle für die Suche im Seitenkopf am Rechner und das Suchfeld oben in
 * „Kunden“ am Handy.
 */
export const SUCH_ZIEL: Record<SearchResult['type'], { label: string; pfad: string }> = {
  customer: { label: 'Kunde', pfad: '/customers' },
  lead: { label: 'Lead', pfad: '/leads' },
  partner: { label: 'Partner', pfad: '/partners' },
  contact: { label: 'Kontakt', pfad: '/contacts' },
};

export function suchZiel(treffer: SearchResult): string | null {
  const ziel = SUCH_ZIEL[treffer.type];
  return ziel ? `${ziel.pfad}/${treffer.id}` : null;
}

/** Treffer zur Eingabe, ab zwei Zeichen und 300 ms nach dem letzten Tippen. */
export function useGlobaleSuche(eingabe: string) {
  const [begriff, setBegriff] = useState(eingabe.trim());
  useEffect(() => {
    const t = window.setTimeout(() => setBegriff(eingabe.trim()), 300);
    return () => window.clearTimeout(t);
  }, [eingabe]);
  const aktiv = begriff.length >= 2;
  const { data, isFetching } = useQuery({
    queryKey: ['search', begriff],
    queryFn: () => searchApi.search(begriff),
    enabled: aktiv,
    staleTime: 30_000,
  });
  return { begriff, aktiv, treffer: aktiv ? (data?.results ?? []) : [], laedt: aktiv && isFetching };
}

/** Trefferliste: Art als kleine Pill, Name, darunter E-Mail oder Beschreibung. */
export function SuchTreffer({
  treffer,
  onWahl,
  ausgewaehlt = -1,
  gross = false,
}: {
  treffer: SearchResult[];
  onWahl: (t: SearchResult) => void;
  /** Mit der Tastatur markierter Treffer (Pfeiltasten im Seitenkopf). */
  ausgewaehlt?: number;
  /** Große Zeilen fürs Handy, mit Pfeil. */
  gross?: boolean;
}) {
  return (
    <ul className={gross ? undefined : 'max-h-80 overflow-y-auto p-1'}>
      {treffer.map((t, i) => (
        <li key={`${t.type}-${t.id}`} className={gross ? 'border-b border-border last:border-b-0' : undefined}>
          <button
            type="button"
            onClick={() => onWahl(t)}
            className={cn(
              'flex w-full cursor-pointer items-start gap-3 text-left transition-colors hover:bg-accent',
              gross ? 'min-h-row items-center px-4 py-2.5' : 'rounded-sm px-2 py-2',
              i === ausgewaehlt && 'bg-accent',
            )}
          >
            <span className={cn('shrink-0 rounded-full bg-muted px-2 py-0.5 t-label text-muted-foreground', !gross && 'mt-0.5')}>
              {SUCH_ZIEL[t.type]?.label || t.type}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate t-body-strong">{t.name}</p>
              {t.email && <p className="truncate t-small text-muted-foreground">{t.email}</p>}
              {t.description && <p className="truncate t-small text-muted-foreground">{t.description}</p>}
            </div>
            {gross && <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted-foreground" />}
          </button>
        </li>
      ))}
    </ul>
  );
}
